import React, { useState, useEffect } from 'react';
import { artAPI } from '../services/api';

const Art = () => {
  const [artPieces, setArtPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchArt = async () => {
    try {
      setLoading(true);
      const response = await artAPI.getAll();
      setArtPieces(response.data);
    } catch (err) {
      console.error('Error fetching art:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArt();
  }, []);

  const handleTwitterClick = (twitterHandle) => {
    // Clean the Twitter handle and create proper URL
    let cleanHandle = twitterHandle;
    if (cleanHandle.startsWith('@')) {
      cleanHandle = cleanHandle.slice(1);
    }
    
    // Check if it's already a full URL or just a username
    let twitterUrl;
    if (cleanHandle.includes('twitter.com') || cleanHandle.includes('x.com')) {
      twitterUrl = cleanHandle.startsWith('http') ? cleanHandle : `https://${cleanHandle}`;
    } else {
      twitterUrl = `https://twitter.com/${cleanHandle}`;
    }
    
    window.open(twitterUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading fan art...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-200 text-lg mb-4">Error loading art pieces</p>
          <p className="text-red-300">{error}</p>
          <button 
            onClick={fetchArt}
            className="mt-4 px-6 py-2 bg-red-700 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Fan Art Gallery
          </h1>
          <p className="text-xl text-red-200 max-w-3xl mx-auto">
            Discover amazing artwork created by our talented ISMP community members. 
            From pixel art to digital masterpieces, our fans bring the server to life through their creativity.
          </p>
        </div>

        {/* Art Gallery */}
        {artPieces.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎨</div>
            <h3 className="text-2xl font-bold text-white mb-2">No Art Pieces Yet</h3>
            <p className="text-red-200">Be the first to share your amazing artwork with the community!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {artPieces.map((art) => (
              <div 
                key={art.id} 
                className="bg-gradient-to-b from-red-800/90 to-red-900/90 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border border-red-600/30 hover:scale-105 transition-all duration-300 hover:shadow-red-500/20"
              >
                {/* Art Image */}
                <div className="aspect-video bg-red-700/20 overflow-hidden">
                  <img 
                    src={art.image} 
                    alt={art.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImciIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPjxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiNkYzI2MjYiLz48c3RvcCBvZmZzZXQ9IjEwMCUiIHN0b3AtY29sb3I9IiM5OTE4MTgiLz48L2xpbmVhckdyYWRpZW50PjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2cpIi8+PHRleHQgeD0iNTAlIiB5PSI0MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtd2VpZ2h0PSJib2xkIj5BcnR3b3JrPC90ZXh0Pjx0ZXh0IHg9IjUwJSIgeT0iNjUlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiNmYmNmY2YiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkZhbiBBcnR3b3JrPC90ZXh0Pjwvc3ZnPg==';
                    }}
                  />
                </div>

                {/* Art Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
                    {art.title}
                  </h3>
                  
                  {art.description && (
                    <p className="text-red-200 mb-4 line-clamp-3">
                      {art.description}
                    </p>
                  )}

                  {/* Artist Info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">
                          {art.artist_name.slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">
                          {art.artist_name}
                        </p>
                        <p className="text-red-300 text-xs">
                          {new Date(art.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {/* Twitter Button */}
                    <button
                      onClick={() => handleTwitterClick(art.artist_twitter)}
                      className="flex items-center space-x-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg transition-colors duration-200 text-sm font-medium"
                      title={`Visit ${art.artist_name}'s Twitter`}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      <span>Twitter</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Art;